import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Audio, Video } from "@remotion/media";
import { Player, type PlayerRef } from "@remotion/player";
import { MediaComponentsProvider, VeysoComposition, cardBox } from "@veyso/remotion";
import type { Rect, Timeline, TimelineLayout } from "@veyso/core";
import { Spinner, timecode } from "../components/ui.js";
import { previewQualities, previewTiers, type PreviewQuality } from "@veyso/core";
import { isTypingInto } from "../lib/keyboard.js";

export interface PreviewProps {
  timeline: Timeline;
  /** Computed once by the editor, so every part of it agrees on frame numbers. */
  layout: TimelineLayout;
  /** When set, the action's target is shown as a draggable box over the frame. */
  editingRect: Rect | null;
  onRectChange: (rect: Rect) => void;
  /**
   * Which box the rectangle is a fraction of.
   *
   * A focus region is aimed at the recording, which the frame may inset onto a
   * background; a card element is placed on the whole frame, because that is
   * what a card fills. One switch rather than two overlays, so the drag, the
   * clamping and the corner handles stay in one place.
   */
  editingSpace?: "body" | "frame";
  /**
   * Controls that float above the box being edited.
   *
   * Passed in rather than built here: the preview knows where the box is, and
   * the editor knows what changing it means. Anchored above the box, and flipped
   * below it when the box is near the top of the frame — a toolbar off the top
   * edge is a toolbar nobody can reach.
   */
  editingToolbar?: React.ReactNode;
  /**
   * An effect is selected but has no region yet.
   *
   * The frame says so and the next drag defines it, which is the difference
   * between aiming an effect and discovering that it was pointing at the middle
   * of the screen all along.
   */
  awaitingRegion?: boolean;
  /**
   * Click the frame to select what is drawn there.
   *
   * The preview reports where the click landed, in the same fractions the
   * boxes are stored in; what is under that point is the editor's question to
   * answer. Set only on a pane where the frame holds selectable things, since
   * the layer that catches the click also stops it reaching the Player.
   */
  onPickAt?: (point: { x: number; y: number }) => void;
  playerRef: React.RefObject<PlayerRef>;
  onFrame: (frame: number) => void;
  /** Which proxy to play. Preview only — the render always uses the master. */
  previewProxyKey?: string;
  debug?: PreviewDebug;
}

/** Four corners, not eight edges: a region here is a button or a panel. */
type Handle = "move" | "nw" | "ne" | "sw" | "se";
type DragMode = Handle | null;

/** Smaller than this and the box is a dot nobody meant to draw. */
const MIN_SIDE = 0.03;

/**
 * The preview mounts the same `VeysoComposition` composition the render worker uses, with
 * the same timeline object. There is no second rendering path and no separate
 * timing logic, so what plays here is what gets encoded.
 */
export const Preview: React.FC<PreviewProps> = ({
  timeline,
  layout,
  editingRect,
  onRectChange,
  editingSpace = "body",
  editingToolbar,
  awaitingRegion = false,
  onPickAt,
  playerRef,
  onFrame,
  previewProxyKey,
  debug,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragMode>(null);

  // Memoised: a fresh object here would hand the Player new props on every
  // render of the editor, which happens on every frame of playback.
  const inputProps = useMemo(
    () => ({ timeline, assetBaseUrl: "/assets", previewProxyKey }),
    [timeline, previewProxyKey],
  );

  // The Player emits frame changes through an event emitter rather than props.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const onFrameUpdate = (event: { detail: { frame: number } }) => onFrame(event.detail.frame);
    player.addEventListener("frameupdate", onFrameUpdate);
    return () => player.removeEventListener("frameupdate", onFrameUpdate);
  }, [playerRef, onFrame]);

  /**
   * Where the recording sits inside the composition.
   *
   * With the frame off this is the whole thing. With it on, the video is inset
   * on its background and the focus overlay is drawn *inside* that card — so a
   * region is a fraction of the recording, not of the finished frame. The
   * editing box has to live in the same space or the two disagree by exactly
   * the inset, which is what put a highlight above the thing it was drawn on.
   */
  const card = useMemo(() => {
    if (editingSpace === "frame") return { left: 0, top: 0, width: 1, height: 1 };
    const body = timeline.sections.body;
    const frame = body.frame;
    if (!frame?.enabled) return { left: 0, top: 0, width: 1, height: 1 };

    const inset = Math.round(layout.width * frame.padding);
    const box = cardBox(layout.width, layout.height, inset, body.aspect);
    return {
      left: (layout.width - box.width) / 2 / layout.width,
      top: (layout.height - box.height) / 2 / layout.height,
      width: box.width / layout.width,
      height: box.height / layout.height,
    };
  }, [timeline, layout.width, layout.height, editingSpace]);

  const pointerToNormalized = useCallback(
    (event: React.PointerEvent | PointerEvent) => {
      const bounds = frameRef.current?.getBoundingClientRect();
      if (!bounds) return null;
      const left = bounds.left + card.left * bounds.width;
      const top = bounds.top + card.top * bounds.height;
      return {
        x: Math.min(1, Math.max(0, (event.clientX - left) / (card.width * bounds.width))),
        y: Math.min(1, Math.max(0, (event.clientY - top) / (card.height * bounds.height))),
      };
    },
    [card],
  );

  /**
   * A click on the frame, as a fraction of the whole frame.
   *
   * Not `pointerToNormalized`, which measures inside whichever box is being
   * edited: a card element is placed on the entire frame whatever else is
   * selected, so picking one has to measure that way too or the hit lands off
   * by the body's inset.
   */
  const pointerToFrame = useCallback((event: React.PointerEvent) => {
    const bounds = frameRef.current?.getBoundingClientRect();
    if (!bounds || !bounds.width || !bounds.height) return null;
    return {
      x: (event.clientX - bounds.left) / bounds.width,
      y: (event.clientY - bounds.top) / bounds.height,
    };
  }, []);

  useEffect(() => {
    if (!drag || !editingRect) return;

    const onMove = (event: PointerEvent) => {
      const point = pointerToNormalized(event);
      if (!point) return;

      if (drag === "move") {
        // Clamp so the box cannot be dragged off the frame.
        onRectChange({
          ...editingRect,
          x: Math.min(1 - editingRect.width, Math.max(0, point.x - editingRect.width / 2)),
          y: Math.min(1 - editingRect.height, Math.max(0, point.y - editingRect.height / 2)),
        });
        return;
      }

      // Each corner moves its own two edges and leaves the opposite corner
      // where it is, which is what makes a box feel pinned rather than
      // rubber-banded from an arbitrary origin.
      const right = editingRect.x + editingRect.width;
      const bottom = editingRect.y + editingRect.height;
      const left = drag === "nw" || drag === "sw" ? Math.min(point.x, right - MIN_SIDE) : editingRect.x;
      const top = drag === "nw" || drag === "ne" ? Math.min(point.y, bottom - MIN_SIDE) : editingRect.y;
      const far = drag === "ne" || drag === "se" ? Math.max(point.x, left + MIN_SIDE) : right;
      const low = drag === "sw" || drag === "se" ? Math.max(point.y, top + MIN_SIDE) : bottom;

      onRectChange({
        x: Math.max(0, left),
        y: Math.max(0, top),
        width: Math.min(1 - Math.max(0, left), far - left),
        height: Math.min(1 - Math.max(0, top), low - top),
      });
    };

    const onUp = () => setDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, editingRect, onRectChange, pointerToNormalized]);

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-bay-900 p-5">
      <div
        ref={frameRef}
        className="relative w-full overflow-hidden rounded-[8px] bg-black outline outline-1 -outline-offset-1 outline-[var(--edge-strong)]"
        style={{
          maxWidth: `min(100%, ${(layout.width / layout.height) * 62}vh)`,
          aspectRatio: `${layout.width} / ${layout.height}`,
        }}
      >
        {/*
          The media package's components, not Remotion's own.
          
          Unwrapped, `useVideoComponent()` falls through to `OffthreadVideo`,
          which is built for the render worker: it decodes frames server-side
          and, in a browser Player, leaves the seek to a plain video element.
          Dragging the playhead then asks that element for an arbitrary time,
          it shows black while it finds a keyframe and decodes forward, and the
          frame arrives whenever it arrives.

          `@remotion/media`'s `<Video>` extracts the exact frame through
          WebCodecs and paints it to a canvas, and it knows when it is
          buffering rather than showing black and hoping. Remotion documents it
          as the one to use in the Player for this reason.

          It is the same pair the browser export already uses — that path
          wrapped the composition and this one never did, which is the whole of
          the difference. An unsupported codec falls back to OffthreadVideo
          here, so this is no worse than before in the cases it cannot help.
        */}
        <MediaComponentsProvider components={{ Video, Audio }}>
          <Player
            ref={playerRef}
            component={VeysoComposition}
            inputProps={inputProps}
            durationInFrames={layout.totalFrames}
            fps={layout.fps}
            compositionWidth={layout.width}
            compositionHeight={layout.height}
            style={{ width: "100%", height: "100%" }}
            acknowledgeRemotionLicense
          />
        </MediaComponentsProvider>

        {debug ? <DebugOverlay debug={debug} /> : null}

        {/* Under the editing box and its handles, which are drawn after it: a
            click on the selected element starts a drag rather than reselecting
            what is already selected. */}
        {onPickAt ? (
          <div
            className="absolute inset-0"
            onPointerDown={(event) => {
              const point = pointerToFrame(event);
              if (point) onPickAt(point);
            }}
          />
        ) : null}

        {/* Nothing aimed yet: the frame says so, and the next drag is the
            region. This replaces inventing a rectangle in the middle and
            leaving someone to discover it was never pointing at anything. */}
        {!editingRect && awaitingRegion ? (
          <div
            className="absolute cursor-crosshair"
            style={{
              left: `${card.left * 100}%`,
              top: `${card.top * 100}%`,
              width: `${card.width * 100}%`,
              height: `${card.height * 100}%`,
            }}
            onPointerDown={(event) => {
              event.preventDefault();
              const origin = pointerToNormalized(event.nativeEvent);
              if (!origin) return;

              const onMove = (move: PointerEvent) => {
                const point = pointerToNormalized(move);
                if (!point) return;
                onRectChange({
                  x: Math.min(origin.x, point.x),
                  y: Math.min(origin.y, point.y),
                  width: Math.max(MIN_SIDE, Math.abs(point.x - origin.x)),
                  height: Math.max(MIN_SIDE, Math.abs(point.y - origin.y)),
                });
              };
              const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
              };
              window.addEventListener("pointermove", onMove);
              window.addEventListener("pointerup", onUp);
            }}
          >
            <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[6px] border border-dashed border-white/40 bg-bay-900/85 px-3 py-1.5 text-[11px] text-ink-100">
              Drag on the frame to choose what this effect points at
            </span>
          </div>
        ) : null}

        {editingRect && editingToolbar ? (
          <div
            className="pointer-events-none absolute z-10 flex justify-center"
            style={{
              left: `${(card.left + editingRect.x * card.width) * 100}%`,
              width: `${editingRect.width * card.width * 100}%`,
              // Above the box, unless there is no room above it.
              top:
                editingRect.y < 0.14
                  ? `${(card.top + (editingRect.y + editingRect.height) * card.height) * 100}%`
                  : undefined,
              bottom: editingRect.y < 0.14 ? undefined : `${(1 - (card.top + editingRect.y * card.height)) * 100}%`,
              paddingTop: editingRect.y < 0.14 ? 8 : 0,
              paddingBottom: editingRect.y < 0.14 ? 0 : 8,
            }}
          >
            {editingToolbar}
          </div>
        ) : null}

        {editingRect ? (
          /* Transparent to the pointer: this wrapper exists to put the box in
             the card's coordinates, and it spans the whole card to do it. Left
             clickable it would swallow every click on the frame — which is what
             stopped one element from being selected while another was, since
             the only live thing under it was the box already selected. */
          <div
            className="pointer-events-none absolute"
            style={{
              left: `${card.left * 100}%`,
              top: `${card.top * 100}%`,
              width: `${card.width * 100}%`,
              height: `${card.height * 100}%`,
            }}
          >
            <div
              onPointerDown={(event) => {
                event.preventDefault();
                setDrag("move");
              }}
              /* No fill. The box is a viewfinder over the footage you are
                 aiming at, and a tint over the picture is the one thing it must
                 not add — you cannot judge what a highlight will look like
                 through a blue wash. An outline reads on light and dark frames
                 alike, and the faint hover tint says it is draggable without
                 colouring the frame while you look at it. */
              className={`pointer-events-auto absolute rounded-[6px] border-2 border-scope-400 bg-transparent transition-colors hover:bg-scope-400/10 ${
                drag === "move" ? "cursor-grabbing" : "cursor-grab"
              }`}
              style={{
                left: `${editingRect.x * 100}%`,
                top: `${editingRect.y * 100}%`,
                width: `${editingRect.width * 100}%`,
                height: `${editingRect.height * 100}%`,
              }}
            >
              <span className="absolute -top-[22px] left-0 rounded-[4px] bg-scope-400 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                focus
              </span>
              {/* One grip per corner, each sized past its visual 12px for a
                  real hit area on a small preview. */}
              {(
                [
                  ["nw", "-top-[6px] -left-[6px] cursor-nwse-resize"],
                  ["ne", "-top-[6px] -right-[6px] cursor-nesw-resize"],
                  ["sw", "-bottom-[6px] -left-[6px] cursor-nesw-resize"],
                  ["se", "-bottom-[6px] -right-[6px] cursor-nwse-resize"],
                ] as const
              ).map(([corner, position]) => (
                <span
                  key={corner}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDrag(corner);
                  }}
                  className={`absolute h-[12px] w-[12px] rounded-[3px] border-2 border-bay-900 bg-scope-400 after:absolute after:-inset-[9px] after:content-[''] ${position}`}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export interface PreviewDebug {
  frame: number;
  fps: number;
  source: string;
  width: number | null;
  effects: number;
  actions: number;
  captions: number;
  onUseOriginal: (value: boolean) => void;
  usingOriginal: boolean;
}

/**
 * Development-only readout of what the preview is actually doing.
 *
 * Hidden unless `import.meta.env.DEV`, because it exists to answer "is the
 * proxy actually being used" during performance work — not to be part of the
 * product. The original/proxy switch is here for the same reason: it is the
 * direct A/B that shows whether the proxy is what helps.
 */
export const DebugOverlay: React.FC<{ debug: PreviewDebug }> = ({ debug }) => {
  if (!import.meta.env.DEV) return null;

  const rows: [string, string][] = [
    ["preview fps", String(debug.fps)],
    ["frame", String(debug.frame)],
    ["source", debug.source],
    ["resolution", debug.width ? `${debug.width}px wide` : "—"],
    ["effects", String(debug.effects)],
    ["actions", String(debug.actions)],
    ["captions", String(debug.captions)],
  ];

  return (
    <div className="pointer-events-auto absolute left-3 top-3 z-20 rounded-[6px] border border-[var(--edge-strong)] bg-bay-850/95 p-2 font-mono shadow-lg text-[10px] leading-[1.5] text-ink-300 backdrop-blur">
      {rows.map(([key, value]) => (
        <div key={key} className="flex gap-2">
          <span className="w-[68px] text-ink-400">{key}</span>
          <span className="text-ink-100">{value}</span>
        </div>
      ))}
      <label className="mt-1.5 flex items-center gap-1.5 border-t border-[var(--edge)] pt-1.5">
        <input
          type="checkbox"
          checked={debug.usingOriginal}
          onChange={(event) => debug.onUseOriginal(event.target.checked)}
        />
        <span>play the 4K master instead</span>
      </label>
    </div>
  );
};

/** Play, scrub, step, mute, fullscreen — and the timecode, which is the readout. */
export const Transport: React.FC<{
  playerRef: React.RefObject<PlayerRef>;
  frame: number;
  totalFrames: number;
  fps: number;
  renderFps: number;
  quality: PreviewQuality;
  onQuality: (quality: PreviewQuality) => void;
  preparingProxy: boolean;
}> = ({ playerRef, frame, totalFrames, fps, renderFps, quality, onQuality, preparingProxy }) => {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    return () => {
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
    };
  }, [playerRef]);

  // Space plays and pauses; arrows step one frame, as in every editor.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingInto(event.target)) return;
      const player = playerRef.current;
      if (!player) return;

      if (event.code === "Space") {
        event.preventDefault();
        player.toggle();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        player.seekTo(Math.max(0, frame - (event.shiftKey ? fps : 1)));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        player.seekTo(Math.min(totalFrames - 1, frame + (event.shiftKey ? fps : 1)));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playerRef, frame, fps, totalFrames]);

  const step = (delta: number) => playerRef.current?.seekTo(Math.min(totalFrames - 1, Math.max(0, frame + delta)));

  return (
    <div className="flex shrink-0 items-center gap-2 border-t border-[var(--edge)] px-4 py-2.5">
      <button className="btn btn-ghost btn-sm" onClick={() => step(-1)} title="Previous frame (←)">
        ◀|
      </button>
      <button
        className="btn btn-secondary btn-sm !h-8 !w-8 !p-0"
        onClick={() => playerRef.current?.toggle()}
        title={playing ? "Pause (Space)" : "Play (Space)"}
      >
        {/* Play triangles need ~2px of optical nudge to look centred. */}
        <span style={{ transform: playing ? undefined : "translateX(1px)" }}>{playing ? "❙❙" : "▶"}</span>
      </button>
      <button className="btn btn-ghost btn-sm" onClick={() => step(1)} title="Next frame (→)">
        |▶
      </button>

      <select
        value={quality}
        onChange={(event) => onQuality(event.target.value as PreviewQuality)}
        title={`${previewTiers[quality].hint} Preview only — the render is always ${renderFps}fps at full resolution.`}
        className="shrink-0 rounded-[4px] border border-[var(--edge)] bg-bay-850 px-1.5 py-0.5 text-[10px] text-ink-300 transition-colors hover:text-ink-100"
      >
        {previewQualities.map((option) => (
          <option key={option} value={option}>
            {previewTiers[option].label}
          </option>
        ))}
      </select>

      {preparingProxy ? (
        <span className="flex shrink-0 items-center gap-1 text-[10px] text-ink-400">
          <Spinner className="!h-3 !w-3" /> preparing
        </span>
      ) : null}

      <input
        type="range"
        aria-label="Seek"
        min={0}
        max={Math.max(1, totalFrames - 1)}
        value={frame}
        onChange={(event) => playerRef.current?.seekTo(Number(event.target.value))}
        className="mx-2 h-1 flex-1 cursor-pointer appearance-none rounded-full bg-bay-700 accent-[var(--color-ember-400)]"
      />

      <span className="tnum shrink-0 whitespace-nowrap text-[12px] text-ink-200">
        {timecode(frame / fps)}
        <span className="text-ink-400"> / {timecode(totalFrames / fps)}</span>
      </span>

      <button
        className="btn btn-ghost btn-sm"
        onClick={() => {
          const next = !muted;
          setMuted(next);
          if (next) playerRef.current?.mute();
          else playerRef.current?.unmute();
        }}
        title={muted ? "Unmute" : "Mute"}
      >
        {muted ? "🔇" : "🔊"}
      </button>
      <input
        type="range"
        aria-label="Volume"
        min={0}
        max={1}
        step={0.05}
        value={volume}
        onChange={(event) => {
          const next = Number(event.target.value);
          setVolume(next);
          playerRef.current?.setVolume(next);
        }}
        className="h-1 w-[64px] cursor-pointer appearance-none rounded-full bg-bay-700 accent-[var(--color-ink-300)]"
      />
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => playerRef.current?.requestFullscreen()}
        title="Fullscreen"
      >
        ⛶
      </button>
    </div>
  );
};
